import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiArrowRight } from 'react-icons/fi';

// Team Images from Public Folder
const founderImg = "/images/founder.jpeg";
const managerImg = "/images/manager.jpeg";
const leadImg = "/images/lead.jpeg";

// ── TEAM DATA ──────────────────────────────────────
const team = [
  {
    name: 'Vimal Raj B',
    role: 'Founder',
    img: founderImg,
    bio: 'Visionary founder with a passion for building premium digital experiences. Leading SitePixel with a focus on innovation, quality, and client success across every project.',
    externalLink: null,
  },
  {
    name: 'Shaidha S',
    role: 'Manager',
    img: managerImg,
    bio: 'Operations and project management expert ensuring every engagement is delivered on time, within scope, and to the highest standard of excellence.',
    externalLink: null,
  },
  {
    name: 'Balaji Narayanan S',
    role: 'Lead Developer',
    img: leadImg,
    bio: 'Creative full-stack developer focused on building scalable, high-performance digital experiences with modern technologies.',
    externalLink: null,
  },
];

const values = [
  {
    title: 'Craftsmanship',
    desc: 'We treat every project as a work of art — built to last, built to impress.'
  },
  {
    title: 'Transparency',
    desc: 'Open communication throughout every phase. No surprises, no excuses.'
  },
  {
    title: 'Innovation',
    desc: 'We stay ahead of the curve so your brand stays ahead of the competition.'
  },
  {
    title: 'Partnership',
    desc: "Your success is our success. We're invested in outcomes, not just output."
  },
];

const TeamMemberCard = ({ member, i }) => {
  const handleClick = () => {
    if (member.externalLink) {
      window.open(member.externalLink, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <motion.div
      key={i}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: i * 0.12 }}
      className="flex flex-col items-center text-center"
      onClick={handleClick}
      style={{ cursor: member.externalLink ? 'pointer' : 'default' }}
    >

      {/* Round profile photo */}
      <div className="relative mb-5 group">

        <div
          className="w-36 h-36 rounded-full overflow-hidden border-2 border-purple-500/30 transition-all duration-400 group-hover:border-purple-400/70 group-hover:shadow-lg"
          style={{
            boxShadow: '0 0 0 4px rgba(127,4,155,0.1)'
          }}
        >
          <img
            src={member.img}
            alt={member.name}
            className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-110"
          />
        </div>

        {member.externalLink && (
          <div
            className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full flex items-center justify-center"
            style={{
              background: 'linear-gradient(135deg, #7f049b, #1c0c19)',
              border: '2px solid rgba(13,8,16,1)'
            }}
          >
            <FiArrowRight
              size={11}
              className="text-white rotate-[-45deg]"
            />
          </div>
        )}
      </div>

      <h3 className="font-display text-xl text-white mb-1">
        {member.name}
      </h3>

      <p className="font-body text-sm text-purple-400 mb-3">
        {member.role}
      </p>

      {member.bio && (
        <p className="font-body text-xs text-white/40 leading-relaxed max-w-xs">
          {member.bio}
        </p>
      )}

      {member.externalLink && (
        <p className="font-body text-xs text-purple-300/60 mt-2">
          View Portfolio ↗
        </p>
      )}
    </motion.div>
  );
};

const AboutPage = () => (
  <div className="min-h-screen pt-32 pb-24">

    {/* Header */}
    <div className="max-w-7xl mx-auto px-6 mb-24">

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">

        <div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="font-mono text-xs text-purple-400 tracking-widest uppercase mb-4"
          >
            About SitePixel
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="font-display text-7xl font-light text-white mb-6 leading-tight"
          >
            We Are a
            <br />
            <em className="accent-text not-italic">Premium</em>
            <br />
            Digital Studio
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="font-body text-white/50 leading-relaxed mb-8"
          >
            Founded in 2018, SitePixel is a boutique digital agency specializing in building premium digital experiences for ambitious brands worldwide. We are a small, elite team of designers, developers, and strategists who believe that exceptional work requires genuine passion.
          </motion.p>

          <Link
            to="/contact"
            className="btn-primary inline-flex items-center gap-2"
          >
            Work With Us <FiArrowRight />
          </Link>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2 }}
          className="relative"
        >

          <div
            className="absolute inset-0 rounded-2xl opacity-30"
            style={{
              background: 'linear-gradient(135deg, #7f049b, #1c0c19)',
              filter: 'blur(30px)'
            }}
          />

          <img
            src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&q=80"
            alt="SitePixel Team"
            className="relative z-10 w-full rounded-2xl object-cover h-80"
          />
        </motion.div>
      </div>
    </div>

    {/* Story */}
    <section className="py-24 relative overflow-hidden">

      <div
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse 60% 60% at 50% 50%, rgba(127,4,155,0.08) 0%, transparent 70%)'
        }}
      />

      <div className="max-w-4xl mx-auto px-6 relative z-10 text-center">

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="font-mono text-xs text-purple-400 tracking-widest uppercase mb-4"
        >
          Our Story
        </motion.p>

        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="font-display text-5xl font-light text-white mb-8"
        >
          Built on a belief that
          <em className="accent-text not-italic">
            {' '}design changes businesses
          </em>
        </motion.h2>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="font-body text-white/50 leading-relaxed text-lg"
        >
          SitePixel began as a two-person studio with a single conviction:
          that most companies were underserved by generic digital solutions.
          We set out to prove that premium design and engineering — the kind
          reserved for Fortune 500 companies — could be accessible to ambitious
          startups and growing brands.
        </motion.p>

      </div>
    </section>

    {/* Mission & Vision */}
    <section className="max-w-7xl mx-auto px-6 py-24">

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

        {[
          {
            label: 'Our Mission',
            text: 'To craft digital experiences that meaningfully elevate brands — combining rigorous strategy, exceptional design, and reliable engineering into work that endures.'
          },
          {
            label: 'Our Vision',
            text: 'A world where every ambitious brand — regardless of size — has access to digital excellence that transforms how they connect with their audience and grow their business.'
          },
        ].map((item, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.1 }}
            className="glass-card p-10"
          >

            <p className="font-mono text-xs text-purple-400 tracking-widest uppercase mb-4">
              {item.label}
            </p>

            <p className="font-display text-2xl font-light text-white/80 leading-relaxed">
              {item.text}
            </p>

          </motion.div>
        ))}
      </div>
    </section>

    {/* Values */}
    <section className="max-w-7xl mx-auto px-6 py-16">

      <div className="text-center mb-14">

        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="font-display text-5xl font-light text-white"
        >
          What We
          <em className="accent-text not-italic">
            {' '}Stand For
          </em>
        </motion.h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">

        {values.map((v, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.1 }}
            className="glass-card p-8 text-center"
          >

            <div
              className="w-12 h-12 rounded-full mx-auto mb-4 flex items-center justify-center font-display text-xl text-purple-400"
              style={{ background: 'rgba(127,4,155,0.2)' }}
            >
              {String(i + 1).padStart(2, '0')}
            </div>

            <h3 className="font-display text-xl text-white mb-2">
              {v.title}
            </h3>

            <p className="font-body text-sm text-white/40 leading-relaxed">
              {v.desc}
            </p>

          </motion.div>
        ))}
      </div>
    </section>

    {/* Team Section */}
    <section className="max-w-7xl mx-auto px-6 py-16">

      <div className="text-center mb-14">

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="font-mono text-xs text-purple-400 tracking-widest uppercase mb-3"
        >
          The Team
        </motion.p>

        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="font-display text-5xl font-light text-white"
        >
          The People Behind
          <em className="accent-text not-italic">
            {' '}the Work
          </em>
        </motion.h2>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-10 max-w-4xl mx-auto">

        {team.map((member, i) => (
          <TeamMemberCard
            key={i}
            member={member}
            i={i}
          />
        ))}

      </div>
    </section>
  </div>
);

export default AboutPage;